/*
 * PEARSON PROPRIETARY AND CONFIDENTIAL INFORMATION SUBJECT TO NDA
 * Copyright © 2019 Pearson Education, Inc.
 * All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Pearson Education, Inc.  The intellectual and technical concepts contained
 * herein are proprietary to Pearson Education, Inc. and may be covered by U.S. and Foreign Patents,
 * patent applications, and are protected by trade secret or copyright law.
 * Dissemination of this information, reproduction of this material, and copying or distribution of this software
 * is strictly forbidden unless prior written permission is obtained
 * from Pearson Education, Inc.
 */
import { applySnapshot, types } from 'mobx-state-tree';
/**
 * A Store to handle User details.
 *
 * @author Hari Gangadharan
 */

const JobProfile = types.model('JobProfile', {
  linkedInUrl: types.optional(types.string, '')
});

const documentSettings = types.model('documentSettings', {
  documentTypes: types.optional(types.string, ''),
  uploadType: types.optional(types.string, '')
});

const Images = types.model('Images', {
  pdfLogo: types.optional(types.string, ''),
  logo: types.optional(types.string, '')
});

const orgConfigs = types.model('orgConfigs', {
  documentSettings: types.maybeNull(documentSettings),
  images: types.optional(Images, {})
});

const Source = types.model('Source',
  {
    type: types.optional(types.string, '')
  });

const Discount = types.model(
  'Discount',
  {
    type: types.maybeNull(types.string)
  }
);

const swapTitle = types.model(
  'swapTitle',
  {
    period: types.maybeNull(types.string),
    count: types.maybeNull(types.number),
    allowSwap: types.maybeNull(types.boolean)
  }
);

const Gpsdetail = types.model(
  'Gpsdetail',
  {
    nextBillingDate: types.maybeNull(types.string),
    canceledAt: types.maybeNull(types.string),
    endDate: types.maybeNull(types.string),
    entitlementLevel: types.maybeNull(types.string),
    entitlementType: types.maybeNull(types.string),
    userId: types.maybeNull(types.string),
    frequency: types.maybeNull(types.string),
    createdAt: types.maybeNull(types.string),
    createdBy: types.maybeNull(types.string),
    subscriptionType: types.maybeNull(types.string),
    frequencyType: types.maybeNull(types.string),
    swapTitleCount: types.maybeNull(types.number),
    maxEntitlements: types.maybeNull(types.integer),
    autoRenew: types.maybeNull(types.boolean),
    subscriptionId: types.maybeNull(types.string),
    productModelId: types.maybeNull(types.string),
    startDate: types.maybeNull(types.string),
    status: types.maybeNull(types.string),
    entitlementTier: types.maybeNull(types.string),
    projectedCancellationDate: types.maybeNull(types.string),
    discount: types.maybeNull(types.array(Discount)),
    subscriptionSource: types.maybeNull(types.string),
    swapTitle: types.maybeNull(types.array(swapTitle))
  }
);

const Details = types.model(
  'Details',
  {
    'Project-Plan-Review-Call': types.optional(types.number, 0),
    'Written-Feedback': types.optional(types.number, 0),
    'Project-Review-Call': types.optional(types.number, 0),
    'Revised-Task-Written-Feedback': types.optional(types.number, 0)
  }
);

const Tasks = types.model(
  'Tasks',
  {
    completedCount: types.optional(types.number, 0),
    totalCount: types.optional(types.number, 0),
    details: types.optional(Details, {})
  }
);

const Badges = types.model(
  'Badges',
  {
    badgeAcceptUrl: types.optional(types.string, ''),
    badgeCreatedDate: types.optional(types.string, ''),
    badgeId: types.optional(types.string, ''),
    badgetType: types.optional(types.string, '')
  }
);

const License = types.model(
  'License',
  {
    licenseId: types.optional(types.string, ''),
    startDate: types.optional(types.string, ''),
    endDate: types.optional(types.string, ''),
    status: types.optional(types.string, '')
  }
);

const User = types.model(
  'User',
  {
    id: types.string,
    firstName: types.string,
    lastName: types.string,
    email: types.string,
    username: types.string,
    initials: types.string,
    token: types.string,
    isAuthenticated: types.boolean,
    emailId: types.maybeNull(types.string),
    roles: types.optional(types.array(types.string), []),
    orgId: types.optional(types.string, ''),
    orgName: types.optional(types.string, ''),
    orgConfigs: types.maybeNull(orgConfigs),
    isAdmin: types.optional(types.boolean, false),
    contextId: types.optional(types.string, ''),
    smsUserId: types.maybeNull(types.string),
    company: types.optional(types.string, ''),
    jobTitle: types.optional(types.string, ''),
    jobProfile: types.maybeNull(JobProfile),
    guidanceTerms: types.optional(types.boolean, false),
    source: types.maybeNull(Source),
    timezone: types.maybeNull(types.string),
    preferredTimezone: types.maybeNull(types.string),
    countryCode: types.optional(types.string, ''),
    gpsSubscriptions: types.maybeNull(types.array(Gpsdetail)),
    ldUserHash: types.maybeNull(types.string),
    isMojoUser: types.maybeNull(types.boolean),
    isImpersonatedId: types.optional(types.boolean, false),
    phoneNumber: types.maybeNull(types.string, ''),
    homeCountryCode: types.maybeNull(types.string),
    customerId: types.maybeNull(types.string),
    verificationStatus: types.maybeNull(types.string),
    tasks: types.optional(Tasks, {}),
    badges: types.optional(types.array(Badges), []),
    license: types.optional(types.array(License), [])
  }
).views((self) => ({
  get fullName() {
    return self.firstName || self.lastName ? `${self.firstName} ${self.lastName}` : '...';
  }
})).actions((self) => ({
  /* eslint-disable no-param-reassign */
  setAuthenticated(id, token, contextId) {
    self.setToken(token);
    self.setId(id);
    self.contextId = contextId;
    self.isAuthenticated = true;
  },

  set(user) {
    applySnapshot(self, { ...self, ...user });
  },

  setId(id) {
    self.id = id;
  },

  setEmailId(emailId) {
    self.emailId = emailId;
  },

  setToken(token) {
    self.token = token;
    self.isAuthenticated = true;
  }
  /* eslint-enable no-param-reassign */
}));

export default User;
